import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Radio,
  Users,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  Send,
  Bell,
  Megaphone,
  AlertTriangle,
  Sparkles,
  RefreshCw,
  Search,
  ExternalLink,
  Volume2,
  VolumeX,
  CheckCircle2,
  XCircle,
  Eye,
  Clock,
  Zap,
  Activity,
  Layers,
  MapPin,
  Shield,
  Trash2,
  MessageSquare,
  Flame,
  Check
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLiveAnalytics } from "@/context/LiveAnalyticsContext";

export function LiveAdminTab() {
  const { subscribeAdminLiveStats, playChime } = useLiveAnalytics();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState("all");

  // Broadcast Notification Form State
  const [broadcastTarget, setBroadcastTarget] = useState("all"); // "all" | "registered" | "guests" | "user"
  const [targetSessionId, setTargetSessionId] = useState("");
  const [targetUserName, setTargetUserName] = useState("");
  const [notifTitle, setNotifTitle] = useState("🎬 ¡Novedad en La Caja LGBT!");
  const [notifMessage, setNotifMessage] = useState("");
  const [notifType, setNotifType] = useState("announcement"); // "announcement" | "info" | "urgent" | "warning" | "success"
  const [actionText, setActionText] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [playSound, setPlaySound] = useState(true);
  const [durationSecs, setDurationSecs] = useState(8);
  const [saveAsOnConnect, setSaveAsOnConnect] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);

  // On-Connect Settings Modal / Drawer
  const [onConnectConfig, setOnConnectConfig] = useState({
    enabled: false,
    title: "",
    message: "",
    type: "info",
    actionText: "",
    actionUrl: "",
    playSound: true
  });
  const [savingOnConnect, setSavingOnConnect] = useState(false);

  // Load initial stats via REST API
  const fetchLiveStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/live/stats");
      setStats(data);
      if (data.onConnectBroadcast) {
        setOnConnectConfig(data.onConnectBroadcast);
      }
      setLastRefreshed(new Date());
    } catch (err) {
      console.warn("Error fetching live stats:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch On-Connect config
  const fetchOnConnectConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/live/on-connect");
      setOnConnectConfig(data);
    } catch (err) {
      console.warn("Error fetching on-connect config:", err);
    }
  }, []);

  useEffect(() => {
    fetchLiveStats();
    fetchOnConnectConfig();

    // Subscribe to real-time WebSocket push updates
    const unsubscribe = subscribeAdminLiveStats((liveData) => {
      setStats(liveData);
      if (liveData.onConnectBroadcast) {
        setOnConnectConfig(liveData.onConnectBroadcast);
      }
      setLastRefreshed(new Date());
      setLoading(false);
    });

    // Fallback polling interval every 8 seconds in case socket drops
    const interval = setInterval(fetchLiveStats, 8000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [fetchLiveStats, fetchOnConnectConfig, subscribeAdminLiveStats]);

  // Handle Send Live Notification
  const handleSendBroadcast = async (e) => {
    if (e) e.preventDefault();

    if (!notifMessage.trim() && !notifTitle.trim()) {
      toast.error("Por favor escribe un título o mensaje para la notificación");
      return;
    }

    if (broadcastTarget === "user" && !targetSessionId) {
      toast.error("Debes seleccionar o ingresar la sesión del usuario destinatario");
      return;
    }

    setSendingBroadcast(true);
    try {
      const payload = {
        target: broadcastTarget,
        sessionId: broadcastTarget === "user" ? targetSessionId : null,
        title: notifTitle.trim(),
        message: notifMessage.trim(),
        type: notifType,
        actionText: actionText.trim(),
        actionUrl: actionUrl.trim(),
        playSound,
        duration: Number(durationSecs) * 1000,
        saveAsOnConnect
      };

      const { data } = await api.post("/admin/live/broadcast", payload);

      toast.success(`⚡ Notificación enviada con éxito a ${data.deliveredCount} usuario(s) conectados`);
      
      // If direct message or modal, close it
      setShowBroadcastModal(false);
      
      // Reset direct target if was targeted
      if (broadcastTarget === "user") {
        setBroadcastTarget("all");
        setTargetSessionId("");
        setTargetUserName("");
      }

      fetchLiveStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar la notificación");
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Open Direct Message modal for a specific user
  const openDirectMessage = (userSession) => {
    setBroadcastTarget("user");
    setTargetSessionId(userSession.sessionId);
    setTargetUserName(userSession.name || userSession.email || `Visitante (${userSession.countryName})`);
    setNotifTitle("Hola desde el equipo de La Caja LGBT");
    setNotifMessage(`Hola ${userSession.name || "amigo/a"}, ¿necesitas ayuda explorando nuestro catálogo de cine LGBTQ+?`);
    setNotifType("info");
    setShowBroadcastModal(true);
  };

  // Disconnect user session
  const handleDisconnectSession = async (sessionId) => {
    if (!window.confirm("¿Seguro que deseas forzar la desconexión de esta sesión?")) return;
    try {
      await api.post("/admin/live/disconnect-user", { sessionId });
      toast.success("Sesión desconectada");
      fetchLiveStats();
    } catch (err) {
      toast.error("Error al desconectar sesión");
    }
  };

  // Save On-Connect Config
  const handleSaveOnConnect = async (e) => {
    e.preventDefault();
    setSavingOnConnect(true);
    try {
      await api.post("/admin/live/on-connect", onConnectConfig);
      toast.success("Configuración de bienvenida automática guardada");
      fetchLiveStats();
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSavingOnConnect(false);
    }
  };

  const usersList = stats?.activeUsers || [];

  // Filter users
  const filteredUsers = usersList.filter((u) => {
    if (selectedCountry !== "all" && u.countryCode !== selectedCountry) return false;
    if (userTypeFilter === "registered" && !u.isRegistered) return false;
    if (userTypeFilter === "guests" && u.isRegistered) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = u.name?.toLowerCase().includes(q);
      const matchEmail = u.email?.toLowerCase().includes(q);
      const matchPage = u.currentPage?.toLowerCase().includes(q);
      const matchCountry = u.countryName?.toLowerCase().includes(q);
      const matchCity = u.city?.toLowerCase().includes(q);
      const matchIp = u.ipMasked?.includes(q);
      return matchName || matchEmail || matchPage || matchCountry || matchCity || matchIp;
    }
    return true;
  });

  return (
    <div className="space-y-8" data-testid="live-analytics-admin-tab">
      {/* Top Header with Live Indicator & Action Button */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-zinc-900/60 p-5 rounded-2xl border border-zinc-800 backdrop-blur-sm">
        <div className="flex items-center gap-3.5">
          <div className="relative flex items-center justify-center">
            <span className="animate-ping absolute inline-flex h-4 w-4 rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                <span>Dashboard Live & Telemetría en Tiempo Real</span>
              </h1>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 uppercase tracking-wide">
                En Vivo
              </span>
            </div>
            <p className="text-xs text-zinc-400 mt-0.5">
              Monitoreo continuo de visitantes activos, páginas en visualización y centro de notificaciones instantáneas.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 font-mono">
            <Clock className="w-3.5 h-3.5" />
            <span>Última señal: {lastRefreshed.toLocaleTimeString()}</span>
          </div>

          <button
            onClick={fetchLiveStats}
            className="p-2 rounded-xl bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
            title="Refrescar métricas"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          <button
            onClick={() => {
              setBroadcastTarget("all");
              setTargetSessionId("");
              setTargetUserName("");
              setShowBroadcastModal(true);
            }}
            className="px-4 py-2 rounded-xl bg-[#E11D48] hover:bg-[#BE123C] text-white text-xs font-semibold flex items-center gap-2 shadow-lg shadow-rose-950/40 transition-colors"
          >
            <Megaphone className="w-4 h-4" />
            <span>Enviar Notificación en Vivo</span>
          </button>
        </div>
      </div>

      {/* KPI Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Online Users */}
        <div className="bg-zinc-950/80 border border-zinc-800/80 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Usuarios en Línea</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <Radio className="w-4 h-4 animate-pulse" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-heading text-3xl sm:text-4xl font-bold text-white">
              {stats?.totalOnline ?? 0}
            </span>
            <span className="text-xs text-emerald-400 font-medium">ahora mismo</span>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center justify-between text-xs text-zinc-400">
            <span>👤 Registrados: <strong className="text-white">{stats?.registeredCount ?? 0}</strong></span>
            <span>🕵️ Invitados: <strong className="text-white">{stats?.guestCount ?? 0}</strong></span>
          </div>
        </div>

        {/* Card 2: Active Pages */}
        <div className="bg-zinc-950/80 border border-zinc-800/80 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-sky-500/5 rounded-full blur-2xl pointer-events-none" />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Páginas Activas</span>
            <div className="w-8 h-8 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-heading text-3xl sm:text-4xl font-bold text-white">
              {stats?.topPages?.length ?? 0}
            </span>
            <span className="text-xs text-sky-400 font-medium">secciones vistas</span>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-800/60 text-xs text-zinc-400 truncate">
            <span>Top: <strong className="text-white truncate">{stats?.topPages?.[0]?.page || "Catálogo"}</strong></span>
          </div>
        </div>

        {/* Card 3: Countries */}
        <div className="bg-zinc-950/80 border border-zinc-800/80 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Países Conectados</span>
            <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
              <Globe className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-heading text-3xl sm:text-4xl font-bold text-white">
              {stats?.topCountries?.length ?? 0}
            </span>
            <span className="text-xs text-purple-400 font-medium">naciones</span>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center gap-1.5 text-xs text-zinc-400 overflow-hidden">
            {stats?.topCountries?.slice(0, 4).map((c) => (
              <span key={c.code} className="inline-flex items-center gap-1 bg-zinc-900 px-1.5 py-0.5 rounded text-[11px] border border-zinc-800">
                <span>{c.flag}</span>
                <span className="text-zinc-300 font-mono">{c.count}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Card 4: Devices Share */}
        <div className="bg-zinc-950/80 border border-zinc-800/80 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none" />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Dispositivos</span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
              <Smartphone className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-heading text-3xl sm:text-4xl font-bold text-white">
              {stats?.devices?.mobilePct ?? 0}%
            </span>
            <span className="text-xs text-amber-400 font-medium">Móvil</span>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center justify-between text-xs text-zinc-400">
            <span className="flex items-center gap-1"><Monitor className="w-3 h-3 text-zinc-500" /> Desktop: <strong className="text-white">{stats?.devices?.desktopPct ?? 0}%</strong></span>
            <span className="flex items-center gap-1"><Tablet className="w-3 h-3 text-zinc-500" /> Tablet: <strong className="text-white">{stats?.devices?.tabletPct ?? 0}%</strong></span>
          </div>
        </div>
      </div>

      {/* Main Grid: Live Visuals & Stream */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Real-time Table of Active Sessions */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-2xl p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#E11D48]" />
                  <span>Usuarios Conectados en Directo ({filteredUsers.length})</span>
                </h3>
                <p className="text-xs text-zinc-400">
                  Visualiza qué contenido está consumiendo cada visitante con actualización instantánea.
                </p>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar URL, país, usuario o IP..."
                    className="bg-black/60 border border-zinc-800 rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] w-48 sm:w-56"
                  />
                </div>

                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="bg-black/60 border border-zinc-800 rounded-xl px-2.5 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-[#E11D48]"
                >
                  <option value="all">Todos los Países</option>
                  {stats?.topCountries?.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.name} ({c.count})
                    </option>
                  ))}
                </select>

                <select
                  value={userTypeFilter}
                  onChange={(e) => setUserTypeFilter(e.target.value)}
                  className="bg-black/60 border border-zinc-800 rounded-xl px-2.5 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-[#E11D48]"
                >
                  <option value="all">Todos los tipos</option>
                  <option value="registered">Solo Registrados</option>
                  <option value="guests">Solo Invitados</option>
                </select>
              </div>
            </div>

            {/* Table */}
            {loading && !stats ? (
              <div className="p-12 text-center text-zinc-500 flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-[#E11D48]" />
                <span className="text-xs">Sintonizando señal en vivo...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-10 border border-dashed border-zinc-800/80 rounded-xl text-center">
                <Users className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-xs text-zinc-400">No hay sesiones activas que coincidan con los filtros seleccionados.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-zinc-800/60">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-900/60 text-zinc-400 uppercase text-[10px] tracking-wider">
                      <th className="py-3 px-3.5">Usuario / Estado</th>
                      <th className="py-3 px-3.5">Ubicación</th>
                      <th className="py-3 px-3.5">Página en Pantalla</th>
                      <th className="py-3 px-3.5">Dispositivo & SO</th>
                      <th className="py-3 px-3.5">Tiempo en Vivo</th>
                      <th className="py-3 px-3.5 text-right">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/60 text-zinc-300">
                    {filteredUsers.map((u) => {
                      const isDetail = u.currentPage?.startsWith("/titulo/");
                      const isRevista = u.currentPage?.startsWith("/revista");
                      const isFest = u.currentPage?.startsWith("/festivales");

                      return (
                        <tr key={u.sessionId} className="hover:bg-white/[0.02] transition-colors">
                          {/* User */}
                          <td className="py-3 px-3.5">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-bold text-white shrink-0">
                                {u.isRegistered ? (
                                  u.name?.charAt(0).toUpperCase() || "U"
                                ) : (
                                  <Users className="w-3.5 h-3.5 text-zinc-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-white truncate text-xs flex items-center gap-1.5">
                                  <span>{u.name || "Invitado"}</span>
                                  {u.role === "admin" && (
                                    <span className="px-1 py-0.2 rounded bg-rose-500/20 text-[#E11D48] text-[9px] font-bold border border-rose-500/30">
                                      ADMIN
                                    </span>
                                  )}
                                </p>
                                <p className="text-[10px] text-zinc-500 font-mono truncate">
                                  {u.email || u.ipMasked}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Country / City */}
                          <td className="py-3 px-3.5 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span className="text-base">{u.flag}</span>
                              <div>
                                <p className="font-medium text-zinc-200 text-xs">{u.countryName}</p>
                                <p className="text-[10px] text-zinc-500">{u.city}</p>
                              </div>
                            </div>
                          </td>

                          {/* Current Page */}
                          <td className="py-3 px-3.5 max-w-[200px]">
                            <a
                              href={u.currentPage}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group inline-flex items-center gap-1 font-mono text-[11px] text-[#E11D48] hover:underline truncate max-w-full"
                              title={u.pageTitle || u.currentPage}
                            >
                              <span className="truncate">{u.currentPage}</span>
                              <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            </a>
                            {u.pageTitle && (
                              <p className="text-[10px] text-zinc-400 truncate">{u.pageTitle}</p>
                            )}
                          </td>

                          {/* Device & OS */}
                          <td className="py-3 px-3.5 whitespace-nowrap">
                            <div className="flex items-center gap-1.5 text-zinc-400">
                              {u.device === "mobile" ? (
                                <Smartphone className="w-3.5 h-3.5 text-amber-400" title="Móvil" />
                              ) : u.device === "tablet" ? (
                                <Tablet className="w-3.5 h-3.5 text-purple-400" title="Tablet" />
                              ) : (
                                <Monitor className="w-3.5 h-3.5 text-sky-400" title="Desktop" />
                              )}
                              <span className="text-[11px] text-zinc-300 font-medium">{u.browser}</span>
                              <span className="text-[10px] text-zinc-500">· {u.os}</span>
                            </div>
                          </td>

                          {/* Duration */}
                          <td className="py-3 px-3.5 whitespace-nowrap font-mono text-[11px] text-zinc-400">
                            {u.durationLabel}
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => openDirectMessage(u)}
                                className="px-2.5 py-1 rounded-lg bg-zinc-900 border border-zinc-700 hover:border-[#E11D48] text-zinc-200 hover:text-white text-[11px] font-medium transition-colors flex items-center gap-1"
                                title="Enviar Notificación Directa a este usuario"
                              >
                                <MessageSquare className="w-3 h-3 text-[#E11D48]" />
                                <span>Mensaje</span>
                              </button>
                              <button
                                onClick={() => handleDisconnectSession(u.sessionId)}
                                className="p-1 rounded-lg text-zinc-500 hover:text-rose-400 hover:bg-zinc-800 transition-colors"
                                title="Forzar desconexión de sesión"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Activity Stream Terminal */}
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-sm font-bold text-white flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" />
                <span>Registro de Eventos en Tiempo Real (Live Event Stream)</span>
              </h3>
              <span className="text-[10px] font-mono text-zinc-500">Últimos {stats?.recentEvents?.length ?? 0} eventos</span>
            </div>

            <div className="bg-black/80 rounded-xl border border-zinc-800/80 p-3 max-h-56 overflow-y-auto font-mono text-[11px] space-y-2">
              {stats?.recentEvents && stats.recentEvents.length > 0 ? (
                stats.recentEvents.map((ev) => {
                  const time = new Date(ev.timestamp).toLocaleTimeString();
                  let icon = "⚡";
                  let textColor = "text-zinc-400";

                  if (ev.type === "join") {
                    icon = "🟢";
                    textColor = "text-emerald-400";
                  } else if (ev.type === "leave") {
                    icon = "🔴";
                    textColor = "text-zinc-500";
                  } else if (ev.type === "page_view") {
                    icon = "📄";
                    textColor = "text-sky-400";
                  } else if (ev.type === "broadcast") {
                    icon = "📢";
                    textColor = "text-[#E11D48]";
                  }

                  return (
                    <div key={ev.id} className="flex items-start gap-2 border-b border-zinc-900 pb-1.5 last:border-0 last:pb-0">
                      <span className="text-zinc-600 text-[10px]">{time}</span>
                      <span>{icon}</span>
                      <div className="flex-1 min-w-0">
                        {ev.type === "join" && (
                          <span className={textColor}>
                            <strong>{ev.data.userName}</strong> se ha conectado desde {ev.data.flag} {ev.data.country} ({ev.data.device})
                          </span>
                        )}
                        {ev.type === "page_view" && (
                          <span className={textColor}>
                            {ev.data.flag} <strong>{ev.data.userName}</strong> navegó a <span className="text-white underline">{ev.data.to}</span>
                          </span>
                        )}
                        {ev.type === "leave" && (
                          <span className={textColor}>
                            <strong>{ev.data.userName}</strong> se desconectó después de {ev.data.durationMinutes} min
                          </span>
                        )}
                        {ev.type === "broadcast" && (
                          <span className={textColor}>
                            Emisión global: <strong>"{ev.data.title}"</strong> entregada a {ev.data.deliveredCount} usuarios
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-zinc-600 text-center py-4">Esperando eventos en vivo...</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Col: Top Pages, Countries & Welcome On-Connect Config */}
        <div className="space-y-6">
          {/* Top Pages in Real Time */}
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-2xl p-5 space-y-4">
            <h3 className="font-heading text-sm font-bold text-white flex items-center gap-2">
              <Eye className="w-4 h-4 text-sky-400" />
              <span>Páginas Más Vistas en Vivo</span>
            </h3>

            <div className="space-y-2.5">
              {stats?.topPages && stats.topPages.length > 0 ? (
                stats.topPages.map((p, idx) => {
                  const maxCount = stats.topPages[0]?.count || 1;
                  const pct = Math.round((p.count / maxCount) * 100);

                  return (
                    <div key={p.page} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <a
                          href={p.page}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-zinc-300 hover:text-[#E11D48] truncate max-w-[180px]"
                          title={p.page}
                        >
                          {p.page}
                        </a>
                        <span className="font-bold text-white shrink-0">{p.count} en vivo</span>
                      </div>
                      <div className="w-full h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-sky-500 to-[#E11D48] rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-xs text-zinc-500 text-center py-4">Sin datos de tráfico aún</p>
              )}
            </div>
          </div>

          {/* Top Countries */}
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-2xl p-5 space-y-4">
            <h3 className="font-heading text-sm font-bold text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-purple-400" />
              <span>Audiencia por Países</span>
            </h3>

            <div className="space-y-2.5">
              {stats?.topCountries && stats.topCountries.length > 0 ? (
                stats.topCountries.map((c) => (
                  <div key={c.code} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-zinc-300">
                        <span className="text-sm">{c.flag}</span>
                        <span>{c.name}</span>
                      </span>
                      <span className="font-bold text-white">{c.count} ({c.percentage}%)</span>
                    </div>
                    <div className="w-full h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all duration-500"
                        style={{ width: `${c.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-zinc-500 text-center py-4">Sin datos de países aún</p>
              )}
            </div>
          </div>

          {/* Automatic On-Connect Welcome Notification Setting */}
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-sm font-bold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span>Mensaje Automático de Bienvenida</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={onConnectConfig.enabled}
                  onChange={(e) => setOnConnectConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#E11D48]" />
              </label>
            </div>
            <p className="text-[11px] text-zinc-400">
              Muestra automáticamente una notificación emergente a cualquier usuario que entre a la web.
            </p>

            <form onSubmit={handleSaveOnConnect} className="space-y-3 pt-2 border-t border-zinc-800/80">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-300 mb-1">Título de Bienvenida</label>
                <input
                  type="text"
                  value={onConnectConfig.title}
                  onChange={(e) => setOnConnectConfig((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="¡Bienvenido/a a La Caja LGBT!"
                  className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-300 mb-1">Mensaje</label>
                <textarea
                  rows={2}
                  value={onConnectConfig.message}
                  onChange={(e) => setOnConnectConfig((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder="Descubre las mejores películas, series y documentales LGBTQ+..."
                  className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-300 mb-1">Botón Acción</label>
                  <input
                    type="text"
                    value={onConnectConfig.actionText}
                    onChange={(e) => setOnConnectConfig((prev) => ({ ...prev, actionText: e.target.value }))}
                    placeholder="Explorar Catálogo"
                    className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-300 mb-1">Enlace / Ruta</label>
                  <input
                    type="text"
                    value={onConnectConfig.actionUrl}
                    onChange={(e) => setOnConnectConfig((prev) => ({ ...prev, actionUrl: e.target.value }))}
                    placeholder="/"
                    className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={onConnectConfig.playSound}
                    onChange={(e) => setOnConnectConfig((prev) => ({ ...prev, playSound: e.target.checked }))}
                    className="rounded bg-zinc-800 border-zinc-700 text-[#E11D48] focus:ring-0"
                  />
                  <span>Emitir Chime</span>
                </label>

                <button
                  type="submit"
                  disabled={savingOnConnect}
                  className="px-3.5 py-1.5 rounded-xl bg-zinc-900 border border-zinc-700 hover:border-zinc-500 text-xs font-semibold text-white transition-colors disabled:opacity-50"
                >
                  {savingOnConnect ? "Guardando..." : "Guardar Ajustes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Broadcast / Push Notification Modal */}
      {showBroadcastModal && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setShowBroadcastModal(false)}
        >
          <div
            className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl rounded-2xl p-6 sm:p-8 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-[#E11D48] font-bold">
                  Consola de Emisión Push en Directo
                </span>
                <h2 className="text-xl font-heading font-bold text-white mt-0.5 flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-[#E11D48]" />
                  <span>Enviar Notificación Instantánea</span>
                </h2>
                <p className="text-xs text-zinc-400 mt-1">
                  El mensaje aparecerá inmediatamente en la pantalla de los visitantes conectados vía WebSocket.
                </p>
              </div>
              <button
                onClick={() => setShowBroadcastModal(false)}
                className="p-1.5 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSendBroadcast} className="space-y-5">
              {/* Target Audience */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-2">
                  Destinatarios de la Notificación
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { id: "all", label: "Todos en Vivo", icon: Radio, desc: `${stats?.totalOnline || 0} conectados` },
                    { id: "registered", label: "Registrados", icon: Shield, desc: `${stats?.registeredCount || 0} usuarios` },
                    { id: "guests", label: "Invitados", icon: Users, desc: `${stats?.guestCount || 0} anónimos` },
                    { id: "user", label: "Usuario Único", icon: MessageSquare, desc: targetUserName || "Individual" }
                  ].map((t) => {
                    const IconComp = t.icon;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setBroadcastTarget(t.id)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          broadcastTarget === t.id
                            ? "bg-[#E11D48]/15 border-[#E11D48] text-white shadow-lg shadow-rose-950/30"
                            : "bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-semibold text-xs">
                          <IconComp className="w-3.5 h-3.5 text-[#E11D48]" />
                          <span>{t.label}</span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-1 truncate">{t.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Type Category */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-2">
                  Estilo / Categoría Visual
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "announcement", label: "Anuncio Destacado", color: "bg-[#E11D48]/20 text-[#E11D48] border-[#E11D48]/40" },
                    { id: "success", label: "Novedad / Estreno", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" },
                    { id: "info", label: "Mensaje General", color: "bg-sky-500/20 text-sky-400 border-sky-500/40" },
                    { id: "warning", label: "Aviso de Servicio", color: "bg-amber-500/20 text-amber-400 border-amber-500/40" },
                    { id: "urgent", label: "Urgente", color: "bg-rose-600/20 text-rose-400 border-rose-600/40" }
                  ].map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setNotifType(cat.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                        notifType === cat.id ? `${cat.color} ring-1 ring-white/20` : "bg-zinc-900 border-zinc-800 text-zinc-400"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title & Message */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1">
                    Título del Aviso
                  </label>
                  <input
                    type="text"
                    required
                    value={notifTitle}
                    onChange={(e) => setNotifTitle(e.target.value)}
                    placeholder="Ej: 🎬 ¡Estreno exclusivo disponible en el catálogo!"
                    className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1">
                    Cuerpo del Mensaje
                  </label>
                  <textarea
                    rows={3}
                    required
                    value={notifMessage}
                    onChange={(e) => setNotifMessage(e.target.value)}
                    placeholder="Escribe el texto de la notificación..."
                    className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              {/* Action Button & URL */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-zinc-900/40 p-4 rounded-xl border border-zinc-800">
                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1">
                    Texto del Botón (Opcional)
                  </label>
                  <input
                    type="text"
                    value={actionText}
                    onChange={(e) => setActionText(e.target.value)}
                    placeholder="Ej: Ver Estreno Ahora"
                    className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1">
                    URL o Ruta de Destino
                  </label>
                  <input
                    type="text"
                    value={actionUrl}
                    onChange={(e) => setActionUrl(e.target.value)}
                    placeholder="Ej: /titulo/heartstopper o /festivales"
                    className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                  />
                </div>
              </div>

              {/* Sound & Options */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={playSound}
                      onChange={(e) => setPlaySound(e.target.checked)}
                      className="rounded bg-zinc-800 border-zinc-700 text-[#E11D48] focus:ring-0"
                    />
                    <span className="flex items-center gap-1">
                      <Volume2 className="w-3.5 h-3.5 text-zinc-400" />
                      <span>Alerta Sonora (Chime)</span>
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={() => playChime(notifType)}
                    className="text-[11px] text-zinc-400 hover:text-white underline"
                  >
                    (Probar Sonido)
                  </button>
                </div>

                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveAsOnConnect}
                    onChange={(e) => setSaveAsOnConnect(e.target.checked)}
                    className="rounded bg-zinc-800 border-zinc-700 text-[#E11D48] focus:ring-0"
                  />
                  <span>Fijar también para nuevas conexiones</span>
                </label>
              </div>

              {/* Live Preview Card */}
              <div className="space-y-2 pt-2 border-t border-zinc-800">
                <label className="block text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">
                  Vista Previa en Pantalla del Usuario
                </label>
                <div className="bg-black/60 p-4 rounded-xl border border-zinc-800/80">
                  <div className="bg-zinc-950/95 border border-[#E11D48]/50 rounded-xl p-4 shadow-xl max-w-md mx-auto space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#E11D48]/20 text-[#E11D48] border border-[#E11D48]/30">
                        {notifType.toUpperCase()}
                      </span>
                      <span className="text-[10px] text-zinc-500 font-mono">Ahora</span>
                    </div>
                    <h4 className="font-heading text-sm font-bold text-white">{notifTitle || "Título del Aviso"}</h4>
                    <p className="text-xs text-zinc-300 leading-relaxed">{notifMessage || "El texto de la notificación aparecerá aquí..."}</p>
                    {actionText && (
                      <div className="pt-2 flex justify-end">
                        <span className="px-3 py-1 rounded-lg bg-[#E11D48] text-white text-xs font-semibold shadow">
                          {actionText}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={() => setShowBroadcastModal(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-900 border border-zinc-700 text-xs text-zinc-300 hover:text-white"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={sendingBroadcast}
                  className="px-5 py-2 rounded-xl bg-[#E11D48] hover:bg-[#BE123C] text-white text-xs font-semibold flex items-center gap-2 shadow-lg shadow-rose-950/40 transition-colors disabled:opacity-50"
                >
                  <Send className={`w-3.5 h-3.5 ${sendingBroadcast ? "animate-spin" : ""}`} />
                  <span>{sendingBroadcast ? "Emitiendo en Directo..." : "Emitir Notificación Ahora"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
